/* =====================================================================
   AL MODINA STORE — PRODUCT PAGE (PDP) BEHAVIOUR
   Plain, dependency-free ES5-safe script loaded only on the default
   product template. Everything is delegated off `document` so it keeps
   working after a Shopify theme-editor section re-render.

   Handles:
   1. Category sidebar — accordion (desktop only; hidden <= 1024px)
   2. Media gallery — thumbnail swap + click-to-zoom
   3. Quantity stepper — clamped to the variant inventory
   4. Tabs — horizontal tab nav + deep-link via #hash
   5. Description clamp — measured show more / show less
   6. Ask a question — scrolls to the tabs and opens that panel
   7. Share — native share with a copy-link fallback
   8. Variant change — hot-swaps price, badge, stock, SKU and ATC state out
      of the section render the theme variant picker already fetches
   ===================================================================== */
(function () {
  'use strict'

  /* ===================================================================
     1. CATEGORY SIDEBAR
     ===================================================================
     Desktop only. The column is hidden outright at <= 1024px (no drawer, no
     toggle), so there is no open/close behaviour here at all — only the
     accordion, which cannot be reached below that width.
     =================================================================== */

  /* Accordion. Works for both the top level (`.pv2-cat`) and the third
     level (`.pv2-cat__sub-item`). */
  function toggleCat(caret) {
    var item = caret.closest('.pv2-cat, .pv2-cat__sub-item')
    if (!item) return
    var open = item.classList.toggle('is-open')
    caret.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  /* ===================================================================
     2. MEDIA GALLERY
     =================================================================== */

  function selectMedia(thumb) {
    var media = thumb.closest('[data-pv2-media]')
    if (!media) return
    var id = thumb.getAttribute('data-media-id')

    var thumbs = media.querySelectorAll('[data-pv2-thumb]')
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle('is-active', thumbs[i] === thumb)
      thumbs[i].setAttribute('aria-selected', thumbs[i] === thumb ? 'true' : 'false')
    }

    var slides = media.querySelectorAll('[data-pv2-slide]')
    for (var j = 0; j < slides.length; j++) {
      slides[j].classList.toggle('is-active', slides[j].getAttribute('data-media-id') === id)
    }

    var stage = media.querySelector('[data-pv2-stage]')
    if (stage) stage.classList.remove('is-zoomed')
  }

  function toggleZoom(stage) {
    stage.classList.toggle('is-zoomed')
  }

  /* ===================================================================
     3. QUANTITY STEPPER
     =================================================================== */

  function clampQty(input) {
    var min = parseInt(input.getAttribute('min'), 10)
    if (isNaN(min)) min = 1
    var max = parseInt(input.getAttribute('max'), 10)
    var value = parseInt(input.value, 10)

    if (isNaN(value) || value < min) value = min
    if (!isNaN(max) && max > 0 && value > max) value = max
    input.value = value

    var wrap = input.closest('[data-pv2-qty]')
    if (!wrap) return

    var down = wrap.querySelector('[data-pv2-qty-down]')
    var up = wrap.querySelector('[data-pv2-qty-up]')
    if (down) down.disabled = value <= min
    if (up) up.disabled = !isNaN(max) && max > 0 && value >= max
  }

  function stepQty(button, delta) {
    var wrap = button.closest('[data-pv2-qty]')
    if (!wrap) return
    var input = wrap.querySelector('[data-pv2-qty-input]')
    if (!input) return
    var value = parseInt(input.value, 10)
    if (isNaN(value)) value = 1
    input.value = value + delta
    clampQty(input)
  }

  /* ===================================================================
     4. TABS
     =================================================================== */

  function selectTab(button) {
    var tabs = button.closest('[data-pv2-tabs]')
    if (!tabs) return

    var buttons = tabs.querySelectorAll('[data-pv2-tab]')
    for (var i = 0; i < buttons.length; i++) {
      var active = buttons[i] === button
      buttons[i].classList.toggle('is-active', active)
      buttons[i].setAttribute('aria-selected', active ? 'true' : 'false')
      buttons[i].setAttribute('tabindex', active ? '0' : '-1')
    }

    var target = button.getAttribute('aria-controls')
    var panels = tabs.querySelectorAll('[data-pv2-panel]')
    for (var j = 0; j < panels.length; j++) {
      panels[j].classList.toggle('is-active', panels[j].id === target)
    }

    /* a clamp inside a panel that was hidden could not be measured before now */
    initClamps()
  }

  /* Arrow-key navigation across the tab list, per the ARIA tabs pattern. */
  function moveTab(button, delta) {
    var tabs = button.closest('[data-pv2-tabs]')
    if (!tabs) return
    var buttons = Array.prototype.slice.call(tabs.querySelectorAll('[data-pv2-tab]'))
    var index = buttons.indexOf(button)
    if (index === -1) return
    var next = buttons[(index + delta + buttons.length) % buttons.length]
    selectTab(next)
    next.focus()
  }

  /* Deep-link support. Opens the tab when the fragment is either the panel
     itself (#pv2-panel-...) or anything inside one — which is how the Ask a
     Question tab re-opens after Shopify redirects back with the form id as
     the fragment. */
  function openTabFromHash() {
    var hash = window.location.hash.replace('#', '')
    if (!hash) return

    var target = document.getElementById(hash)
    if (!target) return

    var panel = target.hasAttribute('data-pv2-panel')
      ? target
      : target.closest('[data-pv2-panel]')
    if (!panel) return

    var button = document.querySelector('[data-pv2-tab][aria-controls="' + panel.id + '"]')
    if (button) selectTab(button)
  }

  /* ===================================================================
     5. DESCRIPTION CLAMP — SHOW MORE / SHOW LESS
     ===================================================================
     The collapsed height is measured from the element's real rendered
     line-height rather than hardcoded, so it lands on a whole number of
     lines whatever type scale the tab is using. The toggle only appears
     when the content genuinely overflows.
     =================================================================== */

  var CLAMP_TOLERANCE = 4

  function lineHeightOf(el) {
    var cs = window.getComputedStyle(el)
    var lh = parseFloat(cs.lineHeight)
    /* `normal` does not parse — fall back to the font size x the body ratio */
    if (isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.6
    return lh
  }

  /* Fix 10: a fixed line count cannot reliably reveal a "How to Use" section,
     because across this catalogue it starts anywhere from 39% to 79% of the
     way through a description. So the collapsed height is stretched to the end
     of that section whenever the heading is present — the line count stays as
     the floor. */
  function revealHeight(el) {
    var needle = (el.getAttribute('data-pv2-clamp-reveal') || '').trim().toLowerCase()
    if (!needle) return 0

    var headings = el.querySelectorAll('h1, h2, h3, h4, h5, h6')
    var marker = null
    for (var i = 0; i < headings.length; i++) {
      if (headings[i].textContent.toLowerCase().indexOf(needle) !== -1) {
        marker = headings[i]
        break
      }
    }
    if (!marker) return 0

    /* the section runs until the next heading at the same level or higher */
    var level = parseInt(marker.tagName.charAt(1), 10)
    var last = marker
    var node = marker.nextElementSibling
    while (node) {
      var tag = node.tagName
      if (/^H[1-6]$/.test(tag) && parseInt(tag.charAt(1), 10) <= level) break
      last = node
      node = node.nextElementSibling
    }

    var bottom = last.getBoundingClientRect().bottom
    var top = el.getBoundingClientRect().top
    /* a little air so the last line never sits flush against the fade */
    return Math.ceil(bottom - top + 16)
  }

  function applyClamp(el) {
    /* a panel that is not the active tab has no layout, so measuring it would
       report 0 and wrongly conclude the text fits */
    if (el.offsetParent === null) return

    var wrap = el.closest('[data-pv2-more-wrap]')
    var button = wrap ? wrap.querySelector('[data-pv2-more-btn]') : null
    var lines = parseInt(el.getAttribute('data-pv2-clamp-lines'), 10)
    if (isNaN(lines) || lines < 1) lines = 28

    var collapsed = Math.round(lineHeightOf(el) * lines)

    /* measure unclamped, then decide */
    var wasClamped = el.classList.contains('tabs__prose--clamped')
    var isExpanded = el.classList.contains('is-expanded')
    if (wasClamped) el.classList.remove('tabs__prose--clamped')
    var full = el.scrollHeight
    var reveal = revealHeight(el)
    if (wasClamped && !isExpanded) el.classList.add('tabs__prose--clamped')

    /* never cut off the reveal section, but never shrink below the floor */
    if (reveal > collapsed) collapsed = reveal

    el.style.setProperty('--pv2-clamp-full', full + 'px')

    if (full <= collapsed + CLAMP_TOLERANCE) {
      el.classList.remove('tabs__prose--clamped', 'is-expanded')
      el.style.removeProperty('--pv2-clamp')
      if (button) {
        button.hidden = true
        button.setAttribute('aria-expanded', 'false')
      }
      return
    }

    el.style.setProperty('--pv2-clamp', collapsed + 'px')
    if (!isExpanded) el.classList.add('tabs__prose--clamped')
    if (button) button.hidden = false
  }

  function initClamps() {
    var els = document.querySelectorAll('[data-pv2-clamp]')
    for (var i = 0; i < els.length; i++) applyClamp(els[i])
  }

  function toggleMore(button) {
    var wrap = button.closest('[data-pv2-more-wrap]')
    if (!wrap) return
    var body = wrap.querySelector('[data-pv2-clamp]')
    if (!body) return

    var expanded = button.getAttribute('aria-expanded') === 'true'

    /* refresh the target height in case anything reflowed since load */
    if (!expanded) body.style.setProperty('--pv2-clamp-full', body.scrollHeight + 'px')

    body.classList.toggle('is-expanded', !expanded)
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true')

    var label = button.querySelector('[data-pv2-more-label]')
    if (label) {
      label.textContent = expanded
        ? button.getAttribute('data-label-more') || 'Show more'
        : button.getAttribute('data-label-less') || 'Show less'
    }

    /* collapsing from far down the page would leave the reader stranded */
    if (expanded) {
      var top = wrap.getBoundingClientRect().top
      if (top < 0) {
        window.scrollTo({ top: wrap.getBoundingClientRect().top + window.pageYOffset - stickyOffset(), behavior: 'smooth' })
      }
    }
  }

  /* ===================================================================
     5b. ASK A QUESTION — jump from the buy panel to the tab
     =================================================================== */

  /* the theme header is sticky, so a raw scroll would hide the tab strip */
  function stickyOffset() {
    var header = document.querySelector('.header')
    var height = header ? header.getBoundingClientRect().height : 0
    return height + 16
  }

  function openAskTab() {
    var tabs = document.querySelector('[data-pv2-tabs]')
    if (!tabs) return

    /* identify the panel by its content, not by its label, so renaming the
       tab in the Customize editor cannot break this */
    var form = tabs.querySelector('[data-pv2-panel] .ask-form')
    var panel = form ? form.closest('[data-pv2-panel]') : null
    if (panel) {
      var button = tabs.querySelector('[data-pv2-tab][aria-controls="' + panel.id + '"]')
      if (button) selectTab(button)
    }

    var top = tabs.getBoundingClientRect().top + window.pageYOffset - stickyOffset()
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  }

  /* ===================================================================
     5c. NATIVE SHARE / COPY LINK
     =================================================================== */

  function initShare() {
    var buttons = document.querySelectorAll('[data-pv2-share-native]')
    var canShare = !!navigator.share
    var canCopy = !!(navigator.clipboard && navigator.clipboard.writeText)
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].hidden = !(canShare || canCopy)
    }
  }

  function nativeShare(button) {
    var url = button.getAttribute('data-share-url')
    var title = button.getAttribute('data-share-title') || document.title

    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function () {})
      return
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        var icon = button.querySelector('i')
        if (!icon) return
        var previous = icon.className
        icon.className = 'ph-fill ph-check'
        window.setTimeout(function () {
          icon.className = previous
        }, 1600)
      })
    }
  }

  /* ===================================================================
     6. DELEGATED EVENTS
     =================================================================== */

  document.addEventListener('click', function (event) {
    var target = event.target
    if (!target || typeof target.closest !== 'function') return

    var caret = target.closest('[data-pv2-cat-toggle]')
    if (caret) {
      event.preventDefault()
      toggleCat(caret)
      return
    }

    var thumb = target.closest('[data-pv2-thumb]')
    if (thumb) {
      event.preventDefault()
      selectMedia(thumb)
      return
    }

    var zoom = target.closest('[data-pv2-zoom]')
    if (zoom) {
      event.preventDefault()
      var zoomStage = zoom.closest('[data-pv2-media]').querySelector('[data-pv2-stage]')
      if (zoomStage) toggleZoom(zoomStage)
      return
    }

    /* clicking a zoomed image zooms back out */
    var stage = target.closest('[data-pv2-stage].is-zoomed')
    if (stage) {
      event.preventDefault()
      stage.classList.remove('is-zoomed')
      return
    }

    var down = target.closest('[data-pv2-qty-down]')
    if (down) {
      event.preventDefault()
      stepQty(down, -1)
      return
    }

    var up = target.closest('[data-pv2-qty-up]')
    if (up) {
      event.preventDefault()
      stepQty(up, 1)
      return
    }

    var tab = target.closest('[data-pv2-tab]')
    if (tab) {
      event.preventDefault()
      selectTab(tab)
      return
    }

    var more = target.closest('[data-pv2-more-btn]')
    if (more) {
      event.preventDefault()
      toggleMore(more)
      return
    }

    var ask = target.closest('[data-pv2-ask]')
    if (ask) {
      event.preventDefault()
      openAskTab()
      return
    }

    var share = target.closest('[data-pv2-share-native]')
    if (share) {
      event.preventDefault()
      nativeShare(share)
    }
  })

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-pv2-qty-input]')
    if (input) clampQty(input)
  })

  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-pv2-qty-input]')
    /* clamp the max only while typing; the min is applied on change so the
       field can be cleared mid-edit without snapping back */
    if (!input) return
    var max = parseInt(input.getAttribute('max'), 10)
    var value = parseInt(input.value, 10)
    if (!isNaN(max) && max > 0 && !isNaN(value) && value > max) input.value = max
  })

  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest && event.target.closest('[data-pv2-tab]')
    if (!tab) return
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      moveTab(tab, 1)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      moveTab(tab, -1)
    }
  })

  window.addEventListener('hashchange', openTabFromHash)

  /* a reflow changes how many lines the collapsed height covers */
  var resizeTimer = null
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(initClamps, 200)
  })

  /* late-loading images inside the description change its full height */
  window.addEventListener('load', initClamps)

  /* ===================================================================
     7. VARIANT CHANGE — hot-swap the buy panel
     ===================================================================
     `block.product-variant-picker` fetches
       {product.url}?variant={id}&section_id={its own section id}
     and dispatches `variant:change:<sectionId>:<productId>` with the parsed
     Document. Because the picker lives inside pdp.hero, that Document is
     this section rendered for the new variant — so the reference-exact
     markup can simply be lifted across.
     =================================================================== */

  /* Ordered deliberately: the sold-out badge must be inserted before the
     discount badge so the `--sold ~ --sale` offset rule still matches when
     both are added to an empty stage. */
  var PDP_SWAP = [
    '[data-pv2-price]',
    '[data-pv2-stock]',
    '[data-pv2-sku]',
    '[data-pv2-sold-badge]',
    '[data-pv2-sale-badge]'
  ]

  var PdpHero = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController()
      this.eventName =
        'variant:change:' + this.dataset.sectionId + ':' + this.dataset.productId

      document.addEventListener(this.eventName, this.onVariantChange.bind(this), {
        signal: this.controller.signal
      })
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort()
    }

    onVariantChange(event) {
      var detail = event.detail || {}
      var button = this.querySelector('[data-pv2-addcart]')

      /* the picker fires once immediately with loading:true so the button can
         be locked while the section render is in flight */
      if (detail.loading) {
        if (button) button.disabled = true
        return
      }

      /* no variant matches the chosen options at all */
      if (!detail.variant) {
        this.setButton(button, false, 'unavailable')
        return
      }

      if (detail.html) this.swapFrom(detail.html)

      /* the new variant may have a different inventory ceiling */
      var input = this.querySelector('[data-pv2-qty-input]')
      if (input) {
        var sourceInput = detail.html
          ? detail.html.querySelector('[data-pv2-qty-input]')
          : null
        if (sourceInput) {
          var max = sourceInput.getAttribute('max')
          if (max) input.setAttribute('max', max)
          else input.removeAttribute('max')
        }
        clampQty(input)
      }

      this.setButton(button, detail.variant.available, 'sold')
      this.showVariantMedia(detail.variant)

      var idField = this.querySelector('input[name="id"]')
      if (idField) idField.value = detail.variant.id

      this.clearError()
    }

    /* The add-to-cart row is deliberately NOT swapped — replacing it would
       throw away a quantity the shopper had already typed. Label and disabled
       state are driven off the data-label-* attributes instead. */
    setButton(button, available, unavailableKind) {
      if (!button) return
      button.disabled = !available

      var label = button.querySelector('[data-pv2-addcart-label]')
      if (!label) return

      if (available) {
        label.textContent = button.getAttribute('data-label-add') || 'Add to cart'
      } else if (unavailableKind === 'unavailable') {
        label.textContent = button.getAttribute('data-label-unavailable') || 'Unavailable'
      } else {
        label.textContent = button.getAttribute('data-label-sold') || 'Sold out'
      }
    }

    /* A variant with its own image should bring that image forward. The
       variant JSON the picker parses carries featured_media.id. */
    showVariantMedia(variant) {
      var media = variant && variant.featured_media
      if (!media || !media.id) return
      var thumb = this.querySelector('[data-pv2-thumb][data-media-id="' + media.id + '"]')
      if (thumb && !thumb.classList.contains('is-active')) selectMedia(thumb)
    }

    showError(message) {
      var box = this.querySelector('[data-pv2-error]')
      if (!box) return
      var text = box.querySelector('[data-pv2-error-text]')
      if (text) text.textContent = message
      box.classList.add('is-visible')
    }

    clearError() {
      var box = this.querySelector('[data-pv2-error]')
      if (box) box.classList.remove('is-visible')
    }

    /* Replace each marked region with its counterpart from the new render.
       Regions absent on one side are added or removed so the discount badge
       appears and disappears with the variant. */
    swapFrom(doc) {
      var source = doc.querySelector(
        'pdp-hero[data-section-id="' +
          this.dataset.sectionId +
          '"][data-product-id="' +
          this.dataset.productId +
          '"]'
      )
      if (!source) return

      for (var i = 0; i < PDP_SWAP.length; i++) {
        var selector = PDP_SWAP[i]
        var next = source.querySelector(selector)
        var current = this.querySelector(selector)

        if (next && current) {
          current.outerHTML = next.outerHTML
        } else if (next && !current) {
          this.insertRegion(selector, next)
        } else if (!next && current) {
          current.parentNode.removeChild(current)
        }
      }

      this.sortBadges()
    }

    /* insertRegion appends, so a badge re-added by a variant change would land
       at the bottom of the stack. Each badge carries its rank; re-apply it. */
    sortBadges() {
      var stack = this.querySelector('[data-pv2-slot-target="badges"]')
      if (!stack) return

      var badges = Array.prototype.slice.call(stack.querySelectorAll('[data-pv2-badge-order]'))
      badges
        .sort(function (a, b) {
          return (
            parseInt(a.getAttribute('data-pv2-badge-order'), 10) -
            parseInt(b.getAttribute('data-pv2-badge-order'), 10)
          )
        })
        .forEach(function (badge) {
          stack.appendChild(badge)
        })
    }

    /* A region that did not exist before needs an anchor. Each swappable
       region declares where it belongs via `data-pv2-slot`. */
    insertRegion(selector, node) {
      var slotName = node.getAttribute('data-pv2-slot')
      var slot = slotName ? this.querySelector('[data-pv2-slot-target="' + slotName + '"]') : null
      if (slot) slot.appendChild(node.cloneNode(true))
    }
  }

  if (!window.customElements.get('pdp-hero')) {
    window.customElements.define('pdp-hero', PdpHero)
  }

  /* ===================================================================
     7b. ADD TO CART
     ===================================================================
     `form.product` posts to cart/add.js and then navigates to the cart page
     unless `product-form:cart:navigate` is cancelled. We deliberately do NOT
     cancel it: after adding, the shopper should land on /cart. (An earlier
     revision cancelled it and opened the cart drawer instead.)
     =================================================================== */

  document.addEventListener('product-form:submit:error', function (event) {
    var hero = event.target && event.target.closest && event.target.closest('pdp-hero')
    if (!hero) return

    var error = (event.detail && event.detail.error) || {}
    var message = error.description || error.message || 'Could not add this item to your cart.'
    if (typeof hero.showError === 'function') hero.showError(message)
  })

  /* ===================================================================
     8. LAZY PRODUCT GRIDS
     ===================================================================
     Two grid sources cannot be resolved in Liquid on a product page:

       recommendations - the `recommendations` object is only populated on a
                         request to routes.product_recommendations_url
       vendor          - a vendor is not a collection, so it is only queryable
                         at /collections/vendors?q=... (&view=pdp-vendor-ajax)

     Both render a hidden shell that we fill here. Either way the cards come
     back as `card.product-modina`, identical to the collection grid.
     =================================================================== */

  function loadLazyGrids() {
    var blocks = document.querySelectorAll('[data-pv2-fetch][data-url]')

    for (var i = 0; i < blocks.length; i++) {
      fillLazyGrid(blocks[i])
    }
  }

  function fillLazyGrid(block) {
    if (block.dataset.pv2Loaded) return
    block.dataset.pv2Loaded = 'true'

    fetch(block.getAttribute('data-url'))
      .then(function (response) {
        return response.text()
      })
      .then(function (text) {
        var parsed = new DOMParser().parseFromString(text, 'text/html')
        var incoming = parsed.querySelector('[data-pv2-track]')
        var track = block.querySelector('[data-pv2-track]')
        if (!incoming || !track) return

        var cards = Array.prototype.slice.call(incoming.querySelectorAll('.product-card'))
        var exclude = block.getAttribute('data-exclude-handle')
        var limit = parseInt(block.getAttribute('data-limit'), 10)
        if (isNaN(limit) || limit < 1) limit = 4

        var kept = []
        for (var i = 0; i < cards.length && kept.length < limit; i++) {
          /* the vendor collection includes the product being viewed */
          if (exclude && cardHandle(cards[i]) === exclude) continue
          kept.push(cards[i])
        }

        /* nothing to show — leave the whole block hidden, heading included */
        if (!kept.length) return

        track.innerHTML = ''
        for (var j = 0; j < kept.length; j++) track.appendChild(kept[j])
        block.hidden = false
      })
      .catch(function (error) {
        console.warn('PDP: failed to load product grid:', error)
      })
  }

  /* `card.product-modina` has no id attribute, so identify by product URL. */
  function cardHandle(card) {
    var link = card.querySelector('a[href*="/products/"]')
    if (!link) return null
    var href = link.getAttribute('href') || ''
    return href.split('/products/').pop().split('?')[0].split('/')[0]
  }

  /* ===================================================================
     9. INIT
     =================================================================== */

  function init() {
    loadLazyGrids()
    var inputs = document.querySelectorAll('[data-pv2-qty-input]')
    for (var i = 0; i < inputs.length; i++) clampQty(inputs[i])
    initClamps()
    initShare()
    openTabFromHash()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  /* the theme editor re-renders sections in place */
  document.addEventListener('shopify:section:load', init)
})()
